import possibleDialectValuesPerLocation from 'Assets/possibleDialectValuesPerLocation.json'
import titles from 'Assets/titles'
import { AGE_THRESHOLD } from 'Globals/Constants'
import Button from 'components/Button'
import CustomSelect from 'components/CustomSelect'
import { Icon } from 'components/Icon'
import { useState } from 'react'
import styled from 'styled-components'

interface DialectLocation {
  location: string
  lat: number
  lng: number
  averageScore: number
}
export default ({
  userAnswers,
  preselectedLocation,
  allLocations,
  userAge,
}: {
  userAnswers: {
    title: string
    value: string
  }[]
  preselectedLocation: DialectLocation
  allLocations: DialectLocation[]
  userAge: number
}) => {
  const [selectedLocation, setSelectedLocation] =
    useState<DialectLocation>(preselectedLocation)
  const [showOptions, setShowOptions] = useState(false)
  const selectedLocationData =
    possibleDialectValuesPerLocation[
      selectedLocation.location as keyof typeof possibleDialectValuesPerLocation
    ]
  const percentageKey =
    userAge < AGE_THRESHOLD ? 'percentageYoung' : 'percentageOld'
  Object.entries(selectedLocationData).map(([key, words], index) => {
    const userAnswer = userAnswers[index]
    const sortedWords = words.sort((a, b) => {
      if (a[percentageKey] === b[percentageKey]) {
        if (a.value === userAnswer?.value) {
          return -1
        } else if (b.value === userAnswer?.value) {
          return 1
        }
      }
      return b[percentageKey] - a[percentageKey]
    })
    return [key, sortedWords]
  })
  // we use map to keep the index of the user answers
  const sameWordsAsUserAnswers = Object.entries(selectedLocationData).map(
    ([key, words], index) => {
      return userAnswers.find((answer) => answer.value === words[0].value)
        ? words[0]
        : undefined
    }
  )
  const otherWordsAsUserAnswers = Object.entries(selectedLocationData).map(
    ([key, words], index) => {
      return sameWordsAsUserAnswers[index] ? undefined : words[0]
    }
  )

  return (
    <LocationDetailWrapper>
      <IntroParagraph>
        In <br />{' '}
        <CustomSelect
          options={allLocations.map((location) => location.location)}
          selectedOption={selectedLocation.location}
          onChange={(option) =>
            setSelectedLocation(
              allLocations.find(
                (location) => location.location === option
              ) as DialectLocation
            )
          }
        />
        <br />
        hat die Mehrheit der Studienteilnehmenden folgende Wörter <b>
          gleich
        </b>{' '}
        ausgesprochen wie Sie:
      </IntroParagraph>
      {sameWordsAsUserAnswers && (
        <>
          <LocationDetailWordsSection
            words={sameWordsAsUserAnswers}
            selectedLocationData={selectedLocationData}
            userAnswers={userAnswers}
            percentageKey={percentageKey}
          />
        </>
      )}
      <HorizontalLine />
      <IntroParagraph>
        In
        {' '}<u>{selectedLocation.location}</u>{' '}
        hat die Mehrheit der Studienteilnehmenden folgende Wörter <b>
          anders
        </b>{' '}
        ausgesprochen wie Sie:
      </IntroParagraph>
      <LocationDetailWordsSection
        words={otherWordsAsUserAnswers}
        selectedLocationData={selectedLocationData}
        userAnswers={userAnswers}
        percentageKey={percentageKey}
      />
    </LocationDetailWrapper>
  )
}
const HorizontalLine = styled.hr`
  text-align: center;
  font-size: 2em;
  line-height: 1;
  margin: 0;
  border-top: 2px solid rgba(128, 128, 128, 0.5);
  width: 20%;
  min-width: 250px;
  margin-top: 2em;
  margin-bottom: 1em;
`
const WordsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5em;
  }
  row-gap: 3em;
  align-items: start;
`
const IntroParagraph = styled.div`
  text-align: center;
  font-size: 1.5em;
  padding: 0 1em;
  margin-bottom: 0;
  max-width: 600px;
  @media screen and (max-width: 768px) {
    font-size: 1.2em;
  }
`
const LocationDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2em;
  @media screen and (max-width: 340px) {
    font-size: 0.8em;
  }
`
const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5em;
`
const DetailWordTitle = styled.div`
  font-size: 1.5em;
  font-weight: 700;
`
const TitleAndIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  min-height: 4em;
  img {
    max-width: 4em;
  }
`
const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
  gap: 0.5em;
`
const DetailButtonWrapper = styled.div<{ isFirstPlace?: boolean }>`
  font-size: ${({ isFirstPlace }) => (isFirstPlace ? '1em' : '.75em')};
  font-weight: ${({ isFirstPlace }) => (isFirstPlace ? 'bold' : 'inherit')};
  opacity: ${({ isFirstPlace }) => (isFirstPlace ? '1' : '.75')};
  display: flex;
  align-items: baseline;
  button {
    /* font-weight: inherit; */
    font-size: 1em;
    cursor: default;
    z-index: 2;
    outline-width: 3px;
    &:hover {
      outline-color: inherit;
    }
  }
`
const Percentage = styled.div`
  background: #80808029;
  /* margin: 1px 0; */
  padding: 0.1em;
  line-height: 1.1;
  z-index: 1;
  transform: translateX(-0.3em);
  padding: 0.4em;
  height: 100%;
  padding-left: 0.6em;
  border-radius: 0 0.3em 0.3em 0;
`

function LocationDetailWordsSection({
  words,
  selectedLocationData,
  userAnswers,
  percentageKey,
}: {
  words: (
    | {
        value: string
        percentageYoung: number
        percentageOld: number
      }
    | undefined
  )[]
  selectedLocationData: {
    [x: string]: {
      value: string
      percentageYoung: number
      percentageOld: number
    }[]
  }
  userAnswers: {
    title: string
    value: string
  }[]
  percentageKey: 'percentageYoung' | 'percentageOld'
}) {
  return (
    <WordsSection>
      {words.map((value, index) => {
        if (!value) return
        const title = titles[index]
        const wordsOfSelectedLocation =
          Object.values(selectedLocationData)[index]
        return (
          <DetailSection key={title}>
            <TitleAndIconWrapper>
              <Icon isActive={true} name={title} />
              <DetailWordTitle>{title}</DetailWordTitle>
            </TitleAndIconWrapper>
            <ButtonsWrapper>
              {wordsOfSelectedLocation.map((word) => {
                if (word[percentageKey] <= 0) return
                const userAnswer = userAnswers[index]
                const isFirstPlace =
                  word[percentageKey] ===
                  wordsOfSelectedLocation[0][percentageKey]
                return (
                  <DetailButtonWrapper
                    key={`${title}-${word.value}`}
                    isFirstPlace={isFirstPlace}
                  >
                    <Button
                      className={
                        userAnswer && userAnswer.value === word.value
                          ? 'selected'
                          : ''
                      }
                    >
                      {word.value}
                    </Button>{' '}
                    <Percentage>{word[percentageKey] * 100}% </Percentage>
                  </DetailButtonWrapper>
                )
              })}
              {
                // if the users answer is not in the value, show it here
                userAnswers[index] &&
                  !wordsOfSelectedLocation.find(
                    (word) => word.value === userAnswers[index].value
                  ) && (
                    <DetailButtonWrapper>
                      <Button className='selected'>
                        {userAnswers[index].value}
                      </Button>
                      <Percentage>0%</Percentage>
                    </DetailButtonWrapper>
                  )
              }
            </ButtonsWrapper>
          </DetailSection>
        )
      })}
    </WordsSection>
  )
}
